/**
 * 应用主体框架
 * @author jasonelchen
 */
import React, { FunctionComponent, useMemo, Suspense } from 'react'
import { BrowserRouter, Switch, Route, matchPath, useLocation } from 'react-router-dom'
import { hot } from 'react-hot-loader/root'
import { BackTop, ConfigProvider, Spin } from 'antd'
import classNames from 'classnames'
import zhCN from 'antd/es/locale-provider/zh_CN'

import { SiderBar, Login, ErrorBoundary, ConfigInfo } from '@components'
import { Provider } from '@store'
import { useAppConfigs } from '@hooks'
import InjectAxiosInterceptors from './library/InjectAxiosInterceptors'

const Frame: FunctionComponent<{}> = () => {
  const location = useLocation()
  const { loading, menu, routes } = useAppConfigs()

  const children = useMemo(() => (
    <Switch>
      {routes.map(route => (
        <Route key={route.key} strict {...route} />
      ))}
    </Switch>
  ), [routes])

  const matchedRoute = useMemo(() => routes.find(route => matchPath(location.pathname, route)), [routes, location])
  const { key, standalone, needLogin } = matchedRoute || {}

  if (!matchedRoute) {
    return (
      <Login gettingAuth={loading}>
        <div id="main-app" className={classNames('main-app', 'page-not-found')}>
          {children}
        </div>
      </Login>
    )
  }

  const $page = (
    <>
      {!standalone && <SiderBar menus={menu} />}
      <div id="main-app" className={classNames('main-app', `page-${key}`)}>
        <BackTop target={() => document.getElementById('main-app')} style={{ bottom: 20 }} />
        <Suspense fallback={<div className="login-loading"><Spin spinning tip="加载中..." /></div>}>
          {children}
        </Suspense>
      </div>
    </>
  )

  return needLogin ? (
    <Login>
      {$page}
    </Login>
  ) : $page
}

const App: FunctionComponent<{}> = () => {
  return (
    <ConfigProvider locale={zhCN}>
      <BrowserRouter basename="/">
        <Provider>
          <ErrorBoundary>
            <InjectAxiosInterceptors />
            <Frame />
            <ConfigInfo />
          </ErrorBoundary>
        </Provider>
      </BrowserRouter>
    </ConfigProvider>
  )
}

export default hot(App)
